import { ZfLoadingSpinner } from '@zafire/react';
import type { FunctionComponent, PropsWithChildren } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useActiveDomicile, useContentByTalkingPointIdAndDomicile, useContentByTopicIdAndDomicile, useTopicDetails } from '../../api';
import type { TalkingPointDto } from '../../api/generated/client';
import { useNumberSearchParams } from '../../hooks/useNumberSearchParams';
import type { AdvisoryNeedDto } from '../../interfaces/advisory-need';
import type { INeedContentContextProps } from './need-content-context';
import { needContentContext } from './need-content-context';

/**
 * Provider that delivers all needed data to show the topics/needs contents, including:
 * - topics
 * - talking-points
 * - advisory-needs
 */
export const NeedContentProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
    const { topicId } = useParams();
    const { data: activeTopic } = useTopicDetails(+topicId!);

    const activeDomicile = useActiveDomicile();
    const prevActiveDomicile = useRef(activeDomicile);

    const [talkingPointId, advisoryNeedId, setSearchParams] = useNumberSearchParams('point', 'adv-need');
    const { data: talkingPoints, isRefetching: isTalkingPointRefetch } = useContentByTopicIdAndDomicile(+topicId!);
    const { data: advisoryNeeds, isRefetching: isAdvisoryNeedRefetch } = useContentByTalkingPointIdAndDomicile(talkingPointId, activeDomicile);
    const [activeAdvisoryNeeds, setActiveAdvisoryNeeds] = useState(advisoryNeeds);
    const [activeTalkingPoints, setActiveTalkingPoints] = useState(talkingPoints);
    const [activeTalkingPoint, setActiveTalkingPoint] = useState<TalkingPointDto>();
    const [activeAdvisoryNeed, setActiveAdvisoryNeed] = useState<AdvisoryNeedDto>();

    const handleSetActiveAdvisoryNeed = useCallback(
        (advisoryNeed?: AdvisoryNeedDto) => {
            setSearchParams((sp) => {
                if (advisoryNeed) {
                    sp.set('adv-need', advisoryNeed.id.toString());
                } else {
                    sp.delete('adv-need');
                }

                return sp;
            });
            setActiveAdvisoryNeed(advisoryNeed);
        },
        [setSearchParams, setActiveAdvisoryNeed],
    );

    const handleSetActiveTalkingPoint = useCallback(
        (talkingPoint?: TalkingPointDto) => {
            setSearchParams((sp) => {
                if (talkingPoint) {
                    sp.set('point', talkingPoint.id.toString());
                } else {
                    sp.delete('point');
                }

                return sp;
            });
            setActiveTalkingPoint(talkingPoint);
        },
        [setSearchParams, setActiveTalkingPoint],
    );

    useEffect(() => {
        if (activeDomicile !== prevActiveDomicile.current) {
            handleSetActiveAdvisoryNeed(undefined);
            handleSetActiveTalkingPoint(undefined);
            setActiveAdvisoryNeeds(advisoryNeeds);
            setActiveTalkingPoints(activeTalkingPoints);
            prevActiveDomicile.current = activeDomicile;
        }
    }, [activeDomicile, handleSetActiveAdvisoryNeed, handleSetActiveTalkingPoint, topicId, activeTalkingPoints, advisoryNeeds]);

    const contentContextProps: INeedContentContextProps = {
        activeAdvisoryNeed,
        activeTalkingPoint,
        setActiveAdvisoryNeed: handleSetActiveAdvisoryNeed,
        setActiveTalkingPoint: handleSetActiveTalkingPoint,
        activeTopic,
        talkingPoints,
        isTalkingPointRefetch,
        isAdvisoryNeedRefetch,
    };

    useEffect(() => {
        setActiveAdvisoryNeeds(advisoryNeeds);
        // update active talking point and advisory need from URL in order to open the need detail panel directly when navigating from upcoming list
        const activeTalkingPointFromURL =
            talkingPointId && talkingPoints && talkingPoints.length > 0 ? talkingPoints.find((talkingPoint) => talkingPoint.id === talkingPointId) : undefined;
        const activeAdvisoryNeedFromURL =
            advisoryNeedId && activeAdvisoryNeeds && activeAdvisoryNeeds.length > 0
                ? activeAdvisoryNeeds.find((advisoryNeed) => advisoryNeed.id === advisoryNeedId)
                : undefined;
        if (!activeTalkingPoint && !!activeTalkingPointFromURL && !activeAdvisoryNeed && !!activeAdvisoryNeedFromURL) {
            handleSetActiveTalkingPoint(activeTalkingPointFromURL);
            handleSetActiveAdvisoryNeed(activeAdvisoryNeedFromURL);
        }
    }, [
        activeTalkingPoint,
        activeAdvisoryNeed,
        talkingPointId,
        advisoryNeedId,
        talkingPoints,
        advisoryNeeds,
        handleSetActiveAdvisoryNeed,
        handleSetActiveTalkingPoint,
        activeAdvisoryNeeds,
    ]);

    if (!activeTopic) {
        return <ZfLoadingSpinner style={{ margin: 'auto' }} />;
    }

    return <needContentContext.Provider value={contentContextProps}>{children}</needContentContext.Provider>;
};
